import React, { useState } from 'react';
import { EquipmentPrimaryPhoto } from '../EquipmentPrimaryPhoto';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  ReferenceInput,
  BooleanInput,
  required,
  useRecordContext,
  useNotify,
  useUpdate,
  useGetOne,
  useGetList,
  useInput,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  MenuItem,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { 
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Equipment type choices based on schema enum
const equipmentTypes = [
  { id: 'BACKHOE', name: 'Backhoe' },
  { id: 'BOX_TRUCK', name: 'Box Truck' },
  { id: 'BULLDOZER', name: 'Bulldozer' },
  { id: 'BUS', name: 'Bus' },
  { id: 'CAR', name: 'Car' },
  { id: 'COMPACTOR', name: 'Compactor' },
  { id: 'CONCRETE_MIXER', name: 'Concrete Mixer' },
  { id: 'CONSTRUCTION', name: 'Construction Equipment' },
  { id: 'CRANE', name: 'Crane' },
  { id: 'DELIVERY_VAN', name: 'Delivery Van' },
  { id: 'DUMP_TRUCK', name: 'Dump Truck' },
  { id: 'EXCAVATOR', name: 'Excavator' },
  { id: 'FLATBED', name: 'Flatbed' },
  { id: 'FORKLIFT', name: 'Forklift' },
  { id: 'GARBAGE_TRUCK', name: 'Garbage Truck' },
  { id: 'GRADER', name: 'Grader' },
  { id: 'LOADER', name: 'Loader' },
  { id: 'MOTOR_COACH', name: 'Motor Coach' },
  { id: 'OTHER', name: 'Other' },
  { id: 'PICKUP_TRUCK', name: 'Pickup Truck' },
  { id: 'PUMP_TRUCK', name: 'Pump Truck' },
  { id: 'REFRIGERATED_TRUCK', name: 'Refrigerated Truck' },
  { id: 'SCHOOL_BUS', name: 'School Bus' },
  { id: 'SEMI_TRUCK', name: 'Semi Truck' },
  { id: 'SKID_STEER', name: 'Skid Steer' },
  { id: 'TANKER', name: 'Tanker' },
  { id: 'TOW_TRUCK', name: 'Tow Truck' },
  { id: 'TRACTOR', name: 'Tractor' },
  { id: 'TRAILER', name: 'Trailer' },
  { id: 'TRUCK', name: 'Truck' },
  { id: 'VAN', name: 'Van' },
];

const transmissionTypes = [
  { id: 'MANUAL', name: 'Manual' },
  { id: 'AUTOMATIC', name: 'Automatic' },
];

const ownershipTypes = [
  { id: 'OWNED', name: 'Owned' },
  { id: 'LEASED', name: 'Leased' },
];

const statusChoices = [
  { id: true, name: 'Active' },
  { id: false, name: 'Inactive' },
];

// Province/State choices for jurisdiction
const jurisdictionChoices = [
  // Canadian Provinces
  { id: 'AB', name: 'Alberta' },
  { id: 'BC', name: 'British Columbia' },
  { id: 'MB', name: 'Manitoba' },
  { id: 'NB', name: 'New Brunswick' },
  { id: 'NL', name: 'Newfoundland and Labrador' },
  { id: 'NS', name: 'Nova Scotia' },
  { id: 'ON', name: 'Ontario' },
  { id: 'PE', name: 'Prince Edward Island' },
  { id: 'QC', name: 'Quebec' },
  { id: 'SK', name: 'Saskatchewan' },
  { id: 'NT', name: 'Northwest Territories' },
  { id: 'NU', name: 'Nunavut' },
  { id: 'YT', name: 'Yukon' },
  // US States (common ones - can be expanded)
  { id: 'AL', name: 'Alabama' },
  { id: 'AK', name: 'Alaska' },
  { id: 'AZ', name: 'Arizona' },
  { id: 'AR', name: 'Arkansas' },
  { id: 'CA', name: 'California' },
  { id: 'CO', name: 'Colorado' },
  { id: 'CT', name: 'Connecticut' },
  { id: 'DE', name: 'Delaware' },
  { id: 'FL', name: 'Florida' },
  { id: 'GA', name: 'Georgia' },
  { id: 'HI', name: 'Hawaii' },
  { id: 'ID', name: 'Idaho' },
  { id: 'IL', name: 'Illinois' },
  { id: 'IN', name: 'Indiana' },
  { id: 'IA', name: 'Iowa' },
  { id: 'KS', name: 'Kansas' },
  { id: 'KY', name: 'Kentucky' },
  { id: 'LA', name: 'Louisiana' },
  { id: 'ME', name: 'Maine' },
  { id: 'MD', name: 'Maryland' },
  { id: 'MA', name: 'Massachusetts' },
  { id: 'MI', name: 'Michigan' },
  { id: 'MN', name: 'Minnesota' },
  { id: 'MS', name: 'Mississippi' },
  { id: 'MO', name: 'Missouri' },
  { id: 'MT', name: 'Montana' },
  { id: 'NE', name: 'Nebraska' },
  { id: 'NV', name: 'Nevada' },
  { id: 'NH', name: 'New Hampshire' },
  { id: 'NJ', name: 'New Jersey' },
  { id: 'NM', name: 'New Mexico' },
  { id: 'NY', name: 'New York' },
  { id: 'NC', name: 'North Carolina' },
  { id: 'ND', name: 'North Dakota' },
  { id: 'OH', name: 'Ohio' },
  { id: 'OK', name: 'Oklahoma' },
  { id: 'OR', name: 'Oregon' },
  { id: 'PA', name: 'Pennsylvania' },
  { id: 'RI', name: 'Rhode Island' },
  { id: 'SC', name: 'South Carolina' },
  { id: 'SD', name: 'South Dakota' },
  { id: 'TN', name: 'Tennessee' },
  { id: 'TX', name: 'Texas' },
  { id: 'UT', name: 'Utah' },
  { id: 'VT', name: 'Vermont' },
  { id: 'VA', name: 'Virginia' },
  { id: 'WA', name: 'Washington' },
  { id: 'WV', name: 'West Virginia' },
  { id: 'WI', name: 'Wisconsin' },
  { id: 'WY', name: 'Wyoming' },
];

interface EquipmentFormProps {
  onSubmit?: (data: any) => void;
  defaultValues?: any;
  transform?: (data: any) => any;
}

interface MultiSelectFieldProps {
  source: string;
  reference: string;
  label: string;
  optionText: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({ source, reference, label, optionText }) => {
  const {
    field,
    fieldState: { error },
  } = useInput({ source });
  
  // Load available items from API
  const { data: availableItems = [], isLoading } = useGetList(reference, {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: {}
  });

  const selectedIds = field.value || [];

  const handleAdd = (itemId: number) => {
    if (!selectedIds.includes(itemId)) {
      const newSelection = [...selectedIds, itemId];
      field.onChange(newSelection);
    }
  };

  const handleRemove = (itemId: number) => {
    const newSelection = selectedIds.filter((id: number) => id !== itemId);
    field.onChange(newSelection);
  };

  const selectedItems = selectedIds.map((id: number) => 
    availableItems.find(item => item.id === id)
  ).filter(Boolean);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      
      {/* Selected items display */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {selectedItems.map((item: any) => (
          <Chip
            key={item.id}
            label={item[optionText]}
            onDelete={() => handleRemove(item.id)}
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
        ))}
        {selectedItems.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No {label.toLowerCase()} selected
          </Typography>
        )}
      </Stack>

      {/* Add new dropdown */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          fullWidth
          placeholder={`Select ${label}...`}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value) {
              handleAdd(value);
            }
          }}
          value=""
          sx={{ maxWidth: 300 }}
          disabled={isLoading}
        >
          <MenuItem value="" disabled>
            {isLoading ? 'Loading...' : `Select ${label}...`}
          </MenuItem>
          {availableItems
            .filter(item => !selectedIds.includes(item.id))
            .map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item[optionText]}
              </MenuItem>
            ))}
        </TextField>
      </Box>
      
      {error && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

// File Upload Component based on fortress-web patterns
interface FileUploadProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  helperText?: string;
  source: string;
  onFilesChange?: (files: File[]) => void;
  folderPath?: string;
  maxSizeMB?: number;
  equipmentId?: string | number;
}

interface UploadedFile {
  file: File;
  progress: number;
  uploaded: boolean;
  error?: string;
  url?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({ 
  label, 
  accept = "image/*,.pdf", 
  multiple = false,
  helperText,
  source,
  onFilesChange,
  folderPath = 'equipment-documents',
  maxSizeMB = 30,
  equipmentId
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const notify = useNotify();
  const [update] = useUpdate();
  const record = useRecordContext();

  // Fetch current file if exists
  React.useEffect(() => {
    const fetchCurrentFile = async () => {
      if (!equipmentId) return;
      
      const fieldName = source === 'ownershipPermitFiles' ? 'ownershipPermitFileId' : 'weightRatingFileId';
      const fileId = record?.[fieldName];
      
      if (fileId) {
        try {
          const response = await fetch(`${import.meta.env.VITE_SIMPLE_REST_URL}/files/${fileId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
          });
          
          if (response.ok) {
            const fileData = await response.json();
            setCurrentFile(fileData);
          }
        } catch (error) {
          console.error('Error fetching current file:', error);
        }
      }
    };

    fetchCurrentFile();
  }, [equipmentId, source, record]);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File size must be less than ${maxSizeMB}MB`;
    }
    return null;
  };

  const uploadFileToServer = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('description', `${label} - ${file.name}`);
    formData.append('folderPath', folderPath);

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  const updateEquipmentWithFile = async (fileData: any, fieldName: string) => {
    if (!equipmentId) return;

    try {
      // Get current equipment data
      const currentEquipment = await fetch(`${import.meta.env.VITE_SIMPLE_REST_URL}/equipment/${equipmentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }).then(res => res.json());

      // Update the specific file field
      const updateData = {
        ...currentEquipment,
        [fieldName]: fileData.id || fileData.url || fileData.path,
      };

      await update('equipment', {
        id: equipmentId,
        data: updateData,
        previousData: currentEquipment,
      });

      notify(`${label} saved to equipment record`, { type: 'success' });
    } catch (error: any) {
      notify(`Failed to save ${label} to equipment: ${error.message}`, { type: 'error' });
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // Validate files
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        notify(error, { type: 'error' });
        return;
      }
    }

    setUploading(true);

    const newUploadedFiles: UploadedFile[] = fileArray.map(file => ({
      file,
      progress: 0,
      uploaded: false,
    }));

    setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

    // Upload files one by one with real progress
    for (let i = 0; i < newUploadedFiles.length; i++) {
      const uploadFileItem = newUploadedFiles[i];
      
      try {
        // Start progress tracking
        const progressInterval = setInterval(() => {
          setUploadedFiles(prev => prev.map((f, index) => 
            f.file === uploadFileItem.file ? { ...f, progress: Math.min(f.progress + 5, 80) } : f
          ));
        }, 100);

        // Actual upload to file server
        const result = await uploadFileToServer(uploadFileItem.file);
        
        clearInterval(progressInterval);
        
        // Update progress to 90% while saving to equipment
        setUploadedFiles(prev => prev.map(f => 
          f.file === uploadFileItem.file ? { ...f, progress: 90 } : f
        ));

        // Determine field name based on source
        const fieldName = source === 'ownershipPermitFiles' ? 'ownershipPermitFileId' : 'weightRatingFileId';
        
        // Save file reference to equipment record
        await updateEquipmentWithFile(result, fieldName);
        
        // Mark as fully complete
        setUploadedFiles(prev => prev.map(f => 
          f.file === uploadFileItem.file 
            ? { ...f, progress: 100, uploaded: true, url: result.url || result.path }
            : f
        ));

        // Set as current file and clear uploaded files (since we only allow one file)
        setCurrentFile(result);
        setUploadedFiles([]);

        notify(`${uploadFileItem.file.name} uploaded and saved successfully`, { type: 'success' });
      } catch (error: any) {
        setUploadedFiles(prev => prev.map(f => 
          f.file === uploadFileItem.file 
            ? { ...f, error: error.message, progress: 0 }
            : f
        ));
        notify(`Failed to upload ${uploadFileItem.file.name}: ${error.message}`, { type: 'error' });
      }
    }

    setUploading(false);
    
    // Update parent with all files
    const allFiles = [...uploadedFiles.map(f => f.file), ...fileArray];
    onFilesChange?.(allFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (fileToRemove: File) => {
    setUploadedFiles(prev => prev.filter(f => f.file !== fileToRemove));
    const remainingFiles = uploadedFiles.filter(f => f.file !== fileToRemove).map(f => f.file);
    onFilesChange?.(remainingFiles);
  };

  const handleDeleteCurrentFile = async () => {
    if (!currentFile || !equipmentId) return;

    try {
      const fieldName = source === 'ownershipPermitFiles' ? 'ownershipPermitFileId' : 'weightRatingFileId';
      
      await update('equipment', {
        id: equipmentId,
        data: { [fieldName]: null },
        previousData: record,
      });

      setCurrentFile(null);
      notify('File deleted successfully', { type: 'success' });
    } catch (error: any) {
      notify(`Failed to delete file: ${error.message}`, { type: 'error' });
    }
  };

  const isImage = (filename: string) => {
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  const handleViewFile = () => {
    setViewDialogOpen(true);
  };

  const handleDownload = () => {
    if (currentFile?.url) {
      window.open(currentFile.url, '_blank');
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {helperText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText}
        </Typography>
      )}
      
      {/* Show current file if exists */}
      {currentFile ? (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
          {/* File Preview */}
          <Box 
            sx={{ 
              height: 200, 
              backgroundColor: '#f5f5f5', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f0f0f0' }
            }}
            onClick={handleViewFile}
          >
            {isImage(currentFile.name) ? (
              <img 
                src={currentFile.url} 
                alt={currentFile.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain' 
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <FileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                <Typography variant="body2">{currentFile.name}</Typography>
              </Box>
            )}
          </Box>

          {/* File Info & Actions */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" noWrap title={currentFile.name}>
              {currentFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentFile.size ? `${(currentFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button 
                size="small" 
                startIcon={<ViewIcon />} 
                onClick={handleViewFile}
              >
                View
              </Button>
              <Button 
                size="small" 
                startIcon={<DownloadIcon />} 
                onClick={handleDownload}
              >
                Download
              </Button>
              <Button 
                size="small" 
                startIcon={<UploadIcon />} 
                onClick={() => setCurrentFile(null)}
              >
                Replace
              </Button>
              <Button 
                size="small" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={handleDeleteCurrentFile}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* Drop Zone - only show if no current file */}
          <Card
            sx={{
              border: `2px dashed ${isDragOver ? '#1976d2' : '#e0e0e0'}`,
              backgroundColor: isDragOver ? '#f3f9ff' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: '#f3f9ff',
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <input
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
                disabled={uploading}
              />
              <label htmlFor={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}>
                {uploading ? (
                  <CircularProgress sx={{ fontSize: 48, mb: 2 }} />
                ) : (
                  <UploadIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                )}
                <Typography variant="h6" gutterBottom>
                  {uploading ? 'Uploading...' : 'Drag and drop files here'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {uploading ? 'Please wait...' : 'or click to browse files'}
                </Typography>
                <Button variant="outlined" component="span" disabled={uploading}>
                  Choose Files
                </Button>
              </label>
            </CardContent>
          </Card>

          {/* File List with Progress - only during upload */}
          {uploadedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Uploading:
              </Typography>
              <List dense>
                {uploadedFiles.map((uploadedFile, index) => (
                  <ListItem key={index} sx={{ px: 0, flexDirection: 'column', alignItems: 'stretch' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <FileIcon sx={{ mr: 1, color: uploadedFile.uploaded ? '#4caf50' : '#1976d2' }} />
                      <ListItemText
                        primary={uploadedFile.file.name}
                        secondary={`${(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      {uploadedFile.uploaded && (
                        <CheckIcon sx={{ color: '#4caf50', mr: 1 }} />
                      )}
                    </Box>
                    {uploadedFile.progress > 0 && uploadedFile.progress < 100 && (
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadedFile.progress} 
                        sx={{ mt: 1, width: '100%' }}
                      />
                    )}
                    {uploadedFile.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {uploadedFile.error}
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}

      {/* View Dialog */}
      {currentFile && (
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => setViewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {currentFile.name}
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {isImage(currentFile.name) ? (
              <img 
                src={currentFile.url} 
                alt={currentFile.name}
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <FileIcon sx={{ fontSize: 96, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6">{currentFile.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This file type cannot be previewed. Click download to view the file.
                </Typography>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  Download File
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export const EquipmentForm: React.FC<EquipmentFormProps> = ({
  onSubmit,
  defaultValues = { active: true },
  transform,
}) => {
  const [ownershipPermitFiles, setOwnershipPermitFiles] = useState<File[]>([]);
  const [weightRatingFiles, setWeightRatingFiles] = useState<File[]>([]);
  const record = useRecordContext(); // Get current record for edit mode

  // Transform existing record data for form fields
  const getTransformedDefaultValues = () => {
    const transformed = {
      active: true,
      ...defaultValues,
    };

    // If we have a record (edit mode), transform the utilizations and commodities
    if (record) {
      // Transform utilizations to utilizationIds (from junction table structure)
      if (record.utilizations && Array.isArray(record.utilizations)) {
        transformed.utilizationIds = record.utilizations.map(item => 
          item.utilization ? item.utilization.id : (item.utilizationId || item.id)
        );
      }

      // Transform commodities to commodityIds (from junction table structure)
      if (record.commodities && Array.isArray(record.commodities)) {
        transformed.commodityIds = record.commodities.map(item => 
          item.commodity ? item.commodity.id : (item.commodityId || item.id)
        );
      }
    }

    return transformed;
  };

  const formDefaultValues = getTransformedDefaultValues();

  const handleFormSubmit = onSubmit ? (data: any) => {
    // Add file uploads to form data
    const formDataWithFiles = {
      ...data,
      ownershipPermitFiles,
      weightRatingFiles,
    };

    if (transform) {
      return onSubmit(transform(formDataWithFiles));
    }
    return onSubmit(formDataWithFiles);
  } : undefined;

  const currentYear = new Date().getFullYear();
  const yearChoices = Array.from({ length: 50 }, (_, i) => ({
    id: currentYear - i,
    name: (currentYear - i).toString(),
  }));

  const defaultTransform = (data: any) => {
    // Remove nested objects that shouldn't be sent to the backend
    // Only send the ID fields, not the full nested objects
    const {
      insurance,
      utilizations,
      commodities,
      allFiles,
      assignments,
      fuel,
      incidents,
      inspections,
      maintenance,
      ownershipPermitFile,
      primaryPhoto,
      primaryPhotoFile,
      weightRatingFile,
      ...cleanData
    } = data;

    return cleanData;
  };

  return (
    <SimpleForm 
      onSubmit={handleFormSubmit} 
      defaultValues={formDefaultValues}
      transform={transform || defaultTransform}
    >
      <Box sx={{ width: '100%', maxWidth: 1000 }}>
        {/* Basic Information */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextInput 
                source="name" 
                validate={[required()]} 
                fullWidth
                label="Name"
                placeholder="e.g., 2021 Nissan Sentra"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput 
                source="type" 
                choices={equipmentTypes} 
                validate={[required()]} 
                fullWidth
                label="Vehicle Type"
                variant="outlined"
                margin="dense"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Status */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SelectInput 
                source="active" 
                choices={statusChoices}
                fullWidth
                label="Vehicle Status"
                variant="outlined"
                margin="dense"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Vehicle Details */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Vehicle Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SelectInput 
                source="manufacturedYear" 
                choices={yearChoices}
                fullWidth
                label="Year"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextInput 
                source="make" 
                fullWidth
                label="Make"
                placeholder="e.g., Ford"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextInput 
                source="model" 
                fullWidth
                label="Model"
                placeholder="e.g., F-150"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SelectInput
                source="transmission"
                choices={transmissionTypes}
                fullWidth
                label="Transmission"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <NumberInput
                source="odometer"
                fullWidth
                label="Odometer (KM)"
                variant="outlined"
                margin="dense"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Identification */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Vehicle Identification
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextInput 
                source="licensePlate" 
                fullWidth
                label="License Plate"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectInput 
                source="jurisdiction" 
                choices={jurisdictionChoices}
                fullWidth
                label="Province/State"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextInput 
                source="vin" 
                fullWidth
                label="VIN"
                variant="outlined"
                margin="dense"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Ownership & Financial */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Ownership & Financial Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SelectInput 
                source="ownershipType" 
                choices={ownershipTypes}
                fullWidth
                label="Ownership Type"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateInput 
                source="acquireDate" 
                fullWidth
                label="Acquire Date"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput 
                source="listPriceNew" 
                fullWidth
                label="List Price New"
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput 
                source="actualCashValue" 
                fullWidth
                label="Actual Cash Value"
                variant="outlined"
                margin="dense"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Insurance */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Insurance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the insurance policy that covers this vehicle
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ReferenceInput
                source="insuranceId"
                reference="insurance"
                sort={{ field: 'policyNumber', order: 'ASC' }}
              >
                <SelectInput
                  fullWidth
                  label="Insurance Policy"
                  variant="outlined"
                  margin="dense"
                  optionText={(record) =>
                    `${record.policyNumber} - ${record.insurerName}${record.policyName ? ' (' + record.policyName + ')' : ''}`
                  }
                  helperText="Select an insurance policy or leave blank if none"
                />
              </ReferenceInput>
            </Grid>
          </Grid>
        </Paper>

        {/* CVOR */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            CVOR
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which CVOR this vehicle operates under
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ReferenceInput
                source="cvorId"
                reference="cvors"
                filter={{ includeInactive: true }}
                sort={{ field: 'cvorNscNumber', order: 'ASC' }}
              >
                <SelectInput
                  fullWidth
                  label="CVOR"
                  variant="outlined"
                  margin="dense"
                  optionText={(record) =>
                    `${record.cvorNscNumber} - ${record.legalOperatingName}${record.active === false ? ' (inactive)' : ''}`
                  }
                  helperText="Select the CVOR this vehicle operates under, or leave blank if none"
                />
              </ReferenceInput>
            </Grid>
          </Grid>
        </Paper>

        {/* Job Types (Utilizations) */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Job Type(s)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add categories that describe what type of work is performed with this vehicle.
          </Typography>
          <MultiSelectField
            source="utilizationIds"
            reference="vehicle-utilizations"
            label="Job Types"
            optionText="name"
          />
        </Paper>

        {/* Commodities */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Commodities Hauled
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add commodities that are hauled with the vehicle
          </Typography>
          <MultiSelectField
            source="commodityIds"
            reference="commodities"
            label="Commodities"
            optionText="name"
          />
        </Paper>

        {/* Notes */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Notes
          </Typography>
          <TextInput
            source="notes"
            fullWidth
            multiline
            rows={6}
            label="Additional Notes"
            variant="outlined"
            margin="dense"
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                width: '100%',
                minWidth: '100%'
              },
              '& .MuiInputBase-input': {
                width: '100%'
              }
            }}
          />
        </Paper>

        {/* Primary Photo - Only show if editing existing equipment */}
        {record?.id && (
          <EquipmentPrimaryPhoto equipmentId={record.id} />
        )}

      </Box>
    </SimpleForm>
  );
};